import { createContext, useCallback, useContext, useMemo, useRef, useState, ReactNode } from 'react';
import { driver, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour.css';

// ==============================|| TOUR (SPOTLIGHT) CONTEXT ||============================== //

export type TourStep = {
  /** Seletor CSS do elemento a destacar. Omita para um passo centralizado (intro). */
  element?: string;
  title: string;
  description: string;
  /** Lado preferido do card em relação ao elemento (padrão: 'bottom'). O driver reposiciona se não couber. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Alinhamento do card no eixo do lado (padrão: 'start'). */
  align?: 'start' | 'center' | 'end';
};

export type RegisteredTour = {
  /** Chave única da tela (usada para o "visto" no localStorage). */
  key: string;
  steps: TourStep[];
};

type TourContextValue = {
  /** Há um tour registrado pela tela atual? */
  hasTour: boolean;
  registerTour: (tour: RegisteredTour) => void;
  unregisterTour: (key: string) => void;
  /** Inicia o tour informado, ou o registrado pela tela atual. */
  startTour: (tour?: RegisteredTour) => void;
};

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour deve ser usado dentro de <TourProvider>.');
  return ctx;
}

export function TourProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<RegisteredTour | null>(null);
  const driverRef = useRef<Driver | null>(null);

  const registerTour = useCallback((tour: RegisteredTour) => {
    setCurrent(tour);
  }, []);

  const unregisterTour = useCallback((key: string) => {
    setCurrent((c) => (c?.key === key ? null : c));
  }, []);

  const startTour = useCallback(
    (tour?: RegisteredTour) => {
      const active = tour ?? current;
      if (!active || !active.steps.length) return;

      // Ignora passos cujo elemento não está na tela (evita pular para o vazio).
      const activeSteps = active.steps.filter((s) => !s.element || document.querySelector(s.element));
      const steps: DriveStep[] = activeSteps.map((s) => ({
        element: s.element,
        popover: {
          title: s.title,
          description: s.description,
          // Com alvo, ancora o card ABAIXO do elemento e segue-o (em vez do default
          // 'left' do driver, que jogava o card para a esquerda da tela). O driver
          // re-posiciona sozinho (top/right/left) quando não cabe na viewport.
          // Sem alvo (intro), deixa o driver centralizar — o CSS fixa no topo.
          ...(s.element ? { side: s.side ?? 'bottom', align: s.align ?? 'start' } : {})
        }
      }));
      if (!steps.length) return;

      driverRef.current?.destroy();
      const instance = driver({
        showProgress: true,
        allowClose: true,
        animate: true,
        smoothScroll: true,
        overlayColor: '#0a0a09',
        overlayOpacity: 0.72,
        stagePadding: 8,
        stageRadius: 12,
        popoverOffset: 12,
        popoverClass: 'scale-tour',
        nextBtnText: 'Próximo',
        prevBtnText: 'Anterior',
        doneBtnText: 'Concluir',
        progressText: '{{current}} / {{total}}',
        // Marca o passo SEM elemento (introdução) para fixá-lo no topo via CSS.
        onPopoverRender: (popover, { state }) => {
          const idx = state.activeIndex ?? 0;
          const isModal = !activeSteps[idx]?.element;
          popover?.wrapper?.classList.toggle('scale-tour-top', isModal);
        },
        steps
      });
      driverRef.current = instance;
      instance.drive();
    },
    [current]
  );

  const value = useMemo<TourContextValue>(
    () => ({
      hasTour: !!current && current.steps.length > 0,
      registerTour,
      unregisterTour,
      startTour
    }),
    [current, registerTour, unregisterTour, startTour]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
